import React from 'react';
import { TrustProof } from '../../types/product';

interface TrustProofInputProps {
  label: string;
  value: TrustProof[];
  onChange: (value: TrustProof[]) => void;
  maxItems?: number;
  helpText?: string;
}

// 旧 styled 定義をそのまま Tailwind クラス文字列にした定数。
const CONTAINER = 'mb-6';
const LABEL = 'block mb-2 text-[14px] font-semibold text-[#333]';
const HELP_TEXT = '-mt-1 mb-3 text-[12px] text-[#666]';
const ITEMS_LIST = 'flex flex-col gap-4 mb-4';
const ITEM_CARD = 'p-4 bg-[#f9f9f9] border border-[#e0e0e0] rounded-lg';
const ADD_CARD = 'p-4 mb-3 bg-[#f0f8ff] border-2 border-dashed border-[#4CAF50] rounded-lg';
const ITEM_HEADER = 'flex justify-between items-center mb-3';
const ADD_HEADER = 'm-0 mb-3 text-[14px] font-semibold text-[#333]';
const ITEM_NUMBER = 'text-[12px] font-semibold text-[#666]';
const FIELD_GROUP = 'mb-3 last:mb-0';
const FIELD_LABEL = 'block mb-1.5 text-[13px] font-medium text-[#555]';
const INPUT =
  'w-full px-3 py-2 border border-[#ddd] rounded-md text-[14px] transition-[border-color] duration-200 ease-[ease] focus:outline-none focus:border-[#4CAF50]';
const TEXTAREA =
  'w-full px-3 py-2 border border-[#ddd] rounded-md text-[14px] [font-family:inherit] resize-y transition-[border-color] duration-200 ease-[ease] focus:outline-none focus:border-[#4CAF50]';
const ADD_BUTTON =
  'w-full mt-3 px-5 py-2.5 border-0 rounded-md bg-[#4CAF50] text-white text-[14px] font-medium cursor-pointer transition-[background-color] duration-200 ease-[ease] enabled:hover:bg-[#45a049] disabled:bg-[#ccc] disabled:cursor-not-allowed';
const REMOVE_BUTTON =
  'px-3 py-1.5 border-0 rounded bg-[#ff5252] text-white text-[12px] font-medium cursor-pointer transition-[background-color] duration-200 ease-[ease] hover:bg-[#ff1744]';
const COUNTER = 'text-right text-[12px] text-[#666]';

export const TrustProofInput: React.FC<TrustProofInputProps> = ({
  label,
  value,
  onChange,
  maxItems = 10,
  helpText
}) => {
  const [newItem, setNewItem] = React.useState<TrustProof>({
    statement: '',
    source: '',
    url: ''
  });

  const handleAdd = () => {
    if (newItem.statement?.trim() && value.length < maxItems) {
      onChange([...value, { ...newItem }]);
      setNewItem({ statement: '', source: '', url: '' });
    }
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleUpdate = (index: number, field: keyof TrustProof, fieldValue: string) => {
    const updated = [...value];
    updated[index] = { ...updated[index], [field]: fieldValue };
    onChange(updated);
  };

  return (
    <div className={CONTAINER}>
      <label className={LABEL}>{label}</label>
      {helpText && <p className={HELP_TEXT}>{helpText}</p>}

      {/* 既存アイテムの表示 */}
      {value.length > 0 && (
        <div className={ITEMS_LIST}>
          {value.map((item, index) => (
            <div className={ITEM_CARD} key={index}>
              <div className={ITEM_HEADER}>
                <span className={ITEM_NUMBER}>#{index + 1}</span>
                <button className={REMOVE_BUTTON} type="button" onClick={() => handleRemove(index)}>
                  削除
                </button>
              </div>

              <div className={FIELD_GROUP}>
                <label className={FIELD_LABEL}>信頼の根拠</label>
                <textarea
                  className={TEXTAREA}
                  value={item.statement || ''}
                  onChange={(e) => handleUpdate(index, 'statement', e.target.value)}
                  placeholder="例: 水質検査成績書"
                  rows={2}
                />
              </div>

              <div className={FIELD_GROUP}>
                <label className={FIELD_LABEL}>情報源</label>
                <input
                  className={INPUT}
                  type="text"
                  value={item.source || ''}
                  onChange={(e) => handleUpdate(index, 'source', e.target.value)}
                  placeholder="例: 第三者機関"
                />
              </div>

              <div className={FIELD_GROUP}>
                <label className={FIELD_LABEL}>URL（任意）</label>
                <input
                  className={INPUT}
                  type="url"
                  value={item.url || ''}
                  onChange={(e) => handleUpdate(index, 'url', e.target.value)}
                  placeholder="https://example.com/proof"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 新規追加フォーム */}
      {value.length < maxItems && (
        <div className={ADD_CARD}>
          <h4 className={ADD_HEADER}>新しい信頼の根拠を追加</h4>

          <div className={FIELD_GROUP}>
            <label className={FIELD_LABEL}>信頼の根拠 *</label>
            <textarea
              className={TEXTAREA}
              value={newItem.statement || ''}
              onChange={(e) => setNewItem({ ...newItem, statement: e.target.value })}
              placeholder="例: 水質検査成績書"
              rows={2}
            />
          </div>

          <div className={FIELD_GROUP}>
            <label className={FIELD_LABEL}>情報源</label>
            <input
              className={INPUT}
              type="text"
              value={newItem.source || ''}
              onChange={(e) => setNewItem({ ...newItem, source: e.target.value })}
              placeholder="例: 第三者機関"
            />
          </div>

          <div className={FIELD_GROUP}>
            <label className={FIELD_LABEL}>URL（任意）</label>
            <input
              className={INPUT}
              type="url"
              value={newItem.url || ''}
              onChange={(e) => setNewItem({ ...newItem, url: e.target.value })}
              placeholder="https://example.com/proof"
            />
          </div>

          <button
            className={ADD_BUTTON}
            type="button"
            onClick={handleAdd}
            disabled={!newItem.statement?.trim()}
          >
            追加
          </button>
        </div>
      )}

      <div className={COUNTER}>
        {value.length} / {maxItems}
      </div>
    </div>
  );
};
